import { useEffect, useRef, useState } from 'react'
import { Info, Mail, MessageCircle, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { mainNavigation } from '../navigation'

export function FloatingActions() {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const groupRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dismissFocus = useRef(false)

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !groupRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', closeOutside)
    return () => document.removeEventListener('pointerdown', closeOutside)
  }, [])

  return <aside className={`floating-actions${pathname === '/enquiry' ? ' floating-actions-enquiry' : ''}`} aria-label="Contact and information">
    <div className="desktop-floating-actions">
    <Link className="floating-circle floating-enquiry" to="/enquiry" aria-label="Start Your Enquiry" title="Start Your Enquiry"><Mail size={24} aria-hidden="true" /></Link>
    <a className="floating-circle floating-whatsapp" href="https://wa.me/85264852582" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp: +85264852582" title="Chat on WhatsApp"><img src="/icons/whatsapp.svg" alt="" width="24" height="24" aria-hidden="true" /></a>
    <div className="floating-info" ref={groupRef}
      onPointerEnter={(event) => { if (event.pointerType === 'mouse') setOpen(true) }}
      onPointerLeave={(event) => { if (event.pointerType === 'mouse' && !event.currentTarget.contains(document.activeElement)) setOpen(false) }}
      onFocusCapture={(event) => {
        if (dismissFocus.current) { dismissFocus.current = false; return }
        if (event.target instanceof HTMLElement && event.target.matches(':focus-visible')) setOpen(true)
      }}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false) }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          setOpen(false)
          if (document.activeElement !== triggerRef.current) { dismissFocus.current = true; triggerRef.current?.focus() }
        }
      }}>
      <button ref={triggerRef} className="floating-circle floating-more" aria-label="More information" title="More information" aria-expanded={open} aria-controls="floating-info-links" onClick={(event) => {
        if (event.detail > 0 && window.matchMedia('(hover: hover) and (pointer: fine)').matches) setOpen(true)
        else setOpen((value) => !value)
      }}><Info size={24} aria-hidden="true" /></button>
      <nav id="floating-info-links" className="floating-info-links" aria-label="More information pages" hidden={!open}>
        {mainNavigation.map(([label, path]) => <Link key={path} to={path}>{label}</Link>)}
      </nav>
    </div>
    </div>
    {pathname !== '/enquiry' && <MobileContact />}
  </aside>
}

function MobileContact() {
  const [open, setOpen] = useState(false)
  const groupRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !groupRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', closeOutside)
    return () => document.removeEventListener('pointerdown', closeOutside)
  }, [])
  return <div className="mobile-contact" ref={groupRef}
    onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false) }}
    onKeyDown={(event) => {
      if (event.key === 'Escape' && open) {
        event.preventDefault()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }}>
    <nav id="mobile-contact-links" className="mobile-contact-links" aria-label="Choose a contact method" hidden={!open}>
      <Link to="/enquiry" onClick={() => setOpen(false)}><Mail size={20} aria-hidden="true" /><span><strong>Free case review</strong><small>Share your care needs</small></span></Link>
      <a href="https://wa.me/85264852582" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}><img src="/icons/whatsapp.svg" alt="" width="20" height="20" /><span><strong>Chat on WhatsApp</strong><small>+852 6485 2582</small></span></a>
      <a href="mailto:info@chinacare.cc" onClick={() => setOpen(false)}><Mail size={20} aria-hidden="true" /><span><strong>Email us</strong><small>info@chinacare.cc</small></span></a>
    </nav>
    <button ref={triggerRef} className="button mobile-contact-trigger" aria-expanded={open} aria-controls="mobile-contact-links" onClick={() => setOpen(value => !value)}>
      {open ? <X size={20} aria-hidden="true" /> : <MessageCircle size={20} aria-hidden="true" />}{open ? 'Close contact options' : 'Contact us'}
    </button>
  </div>
}
